import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Users, Search, Check, X } from 'lucide-react';
import {
  fetchClientsByStatus,
  approveClient,
  rejectClient
} from '../../../services/admin/api';
import './client-list.css';

const ClientList = () => {
  const [clients, setClients] = useState([]);
  const [sortColumn, setSortColumn] = useState('username');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();
  const location = useLocation();

  // Detect mode from URL
  const isPending = location.pathname.includes('pending');
  const isRejected = location.pathname.includes('rejected');
  const isApproved = !isPending && !isRejected; // default approved

  const status = isPending
    ? 'PENDING'
    : isRejected
      ? 'REJECTED'
      : 'APPROVED';

  useEffect(() => {
    const fetchClients = async () => {
      try {
        setLoading(true);

        const result = await fetchClientsByStatus(status);
        if (Array.isArray(result)) {
          setClients(result);
        } else {
          setClients([]);
        }
      } catch (error) {
        console.error('Error fetching clients:', error);
        setClients([]);
      } finally {
        setLoading(false);
      }
    };

    fetchClients();
  }, [status]);


  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortOrder('asc');
    }
  };

  const filteredClients = useMemo(() => {
    return clients
      .filter((client) =>
        client.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.phoneNumber?.toString().includes(searchTerm)
      )
      .sort((a, b) => {
        const valA = a[sortColumn];
        const valB = b[sortColumn];

        if (valA == null) return 1;
        if (valB == null) return -1;

        if (typeof valA === 'string' && typeof valB === 'string') {
          return sortOrder === 'asc'
            ? valA.toLowerCase().localeCompare(valB.toLowerCase())
            : valB.toLowerCase().localeCompare(valA.toLowerCase());
        }

        return sortOrder === 'asc'
          ? valA - valB
          : valB - valA;
      });

  }, [searchTerm, sortColumn, sortOrder, clients]);

  const paginatedClients = useMemo(() => {
    const start = currentPage * itemsPerPage;
    return filteredClients.slice(start, start + itemsPerPage);
  }, [filteredClients, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredClients.length / itemsPerPage);

  // ---------------- ACTIONS ----------------

  const handleNavigateToCandidates = (client) => {
    navigate(`/admin/client/${client.email}/candidates`, {
      state: {
        heading: 'Candidates List',
        clientUserId: client.id,
        clientName: client.username,
        companyName: client.companyName,
      },
    });
  };

  const handleApprove = async (id) => {
    await approveClient(id);
    setClients(prev => prev.filter(c => c.id !== id));
  };

  const handleReject = async (id) => {
    await rejectClient(id);
    setClients(prev => prev.filter(c => c.id !== id));
  };

  // ---------------- UI ----------------

  return (
    <div className="client-wrapper">
      <div className="clinet-table-controls">
        <div className="entries-select">
          Show{" "}
          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(0);
            }}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>{" "}
          entries
        </div>

        <div className="client-search-box">
          <div className="client-search-input-wrapper">
            <input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(0);
              }}
            />
            <Search className="client-search-icon" size={18} />
          </div>
        </div>
      </div>

      <div className="clinet-table-container">
        <table className="candidates-clinet-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('username')}>
                Client Name {sortColumn === 'username' ? (sortOrder === 'asc' ? '↑' : '↓') : '↑↓'}
              </th>

              <th onClick={() => handleSort('companyName')}>
                Company {sortColumn === 'companyName' ? (sortOrder === 'asc' ? '↑' : '↓') : '↑↓'}
              </th>

              <th onClick={() => handleSort('email')}>
                Email {sortColumn === 'email' ? (sortOrder === 'asc' ? '↑' : '↓') : '↑↓'}
              </th>

              <th onClick={() => handleSort('phoneNumber')}>
                Contact Number {sortColumn === 'phoneNumber' ? (sortOrder === 'asc' ? '↑' : '↓') : '↑↓'}
              </th>

              {!isRejected && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="5" className="no-data loading-text">
                  Loading Clients...
                </td>
              </tr>
            ) : paginatedClients.length > 0 ? (
              paginatedClients.map((client) => (
                <tr key={client.id}>
                  <td>{client.username}</td>
                  <td>{client.companyName}</td>
                  <td>{client.email}</td>
                  <td>{client.phoneNumber}</td>

                  {!isRejected && (
                    <td>
                      {isPending && (
                        <>
                          <button
                            className="candidate-btn"
                            title="Approve"
                            onClick={() => handleApprove(client.id)}
                          >
                            <Check size={16} />
                          </button>

                          <button
                            className="candidate-btn"
                            title="Reject"
                            onClick={() => handleReject(client.id)}
                          >
                            <X size={16} />
                          </button>
                        </>
                      )}

                      {isApproved && (
                        <button
                          className="candidate-btn"
                          title="View Candidates"
                          onClick={() => handleNavigateToCandidates(client)}
                        >
                          <Users size={18} />
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="no-data">
                  No matching clients found.
                </td>
              </tr>
            )}
          </tbody>

        </table>
      </div>

      <div className="pagination">
        <div className="pagination-summary">
          Showing {filteredClients.length === 0 ? 0 : currentPage * itemsPerPage + 1} to{" "}
          {Math.min((currentPage + 1) * itemsPerPage, filteredClients.length)} of{" "}
          {filteredClients.length} entries
        </div>

        <div className="pagination-buttons-user">
          <button
            className="page-btn"
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
            disabled={currentPage === 0}
          >
            ←
          </button>
          <button className="page-btn active">{currentPage + 1}</button>
          <button
            className="page-btn"
            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1))}
            disabled={currentPage >= totalPages - 1 || totalPages === 0}
          >
            →
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClientList;
